<template>
  <main class="min-h-screen bg-gradient-to-b from-background to-background/95 overflow-hidden">
    <UnderwaterBg class="fixed inset-0 z-0" />
    
    <div class="relative z-10 p-3 sm:p-4 lg:p-6 max-w-7xl mx-auto firefox-optimize">
      <div class="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        <!-- Header Section -->
        <admin-header 
          :username="username || undefined"
          @new-cruise="redirectToPricing"
          @logout="handleLogout"
          @upgrade="dialogState.selectPackage = true"
        />

        <!-- Duck Activity Section (moved to top) -->
        <div v-if="!loading && (ducksFoundByRarity || ducksHiddenByRarity)" class="mb-6">
          <div 
            class="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 sm:p-6 cursor-pointer hover:shadow-md transition-all duration-200"
            @click="toggleDuckActivity"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <v-icon color="primary" size="24">mdi-duck</v-icon>
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-blue-900">Your Duck Activity</h3>
                  <div class="flex flex-wrap gap-x-4 gap-y-1 text-sm text-blue-700">
                    <span v-if="ducksFoundByRarity" class="inline-flex items-center gap-2">
                      <span><span class="font-medium">{{ getTotalFromRarity(ducksFoundByRarity) }}</span> found</span>
                      <span class="inline-flex items-center gap-1 text-amber-700">
                        <v-icon size="16" color="warning">mdi-trophy</v-icon>
                        <span class="font-semibold">{{ getWeightedScoreFromRarity(ducksFoundByRarity) }}</span>
                      </span>
                    </span>
                    <span v-if="ducksHiddenByRarity" class="inline-flex items-center gap-2">
                      <span><span class="font-medium">{{ getTotalFromRarity(ducksHiddenByRarity) }}</span> hidden</span>
                      <span class="inline-flex items-center gap-1 text-amber-700">
                        <v-icon size="16" color="warning">mdi-trophy</v-icon>
                        <span class="font-semibold">{{ getWeightedScoreFromRarity(ducksHiddenByRarity) }}</span>
                      </span>
                    </span>
                  </div>
                  <!-- Leaderboard Ranking Chips -->
                  <div v-if="leaderboardRanking" class="flex flex-wrap gap-2 mt-2">
                    <div 
                      v-if="leaderboardRanking.finderRank !== null" 
                      class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-100 text-emerald-800 rounded-lg text-xs font-semibold border border-emerald-200"
                    >
                      <v-icon size="14" color="success">mdi-magnify</v-icon>
                      <span>Finder</span>
                      <span class="bg-emerald-200 text-emerald-900 px-1.5 py-0.5 rounded text-xs font-bold">
                        #{{ leaderboardRanking.finderRank }}
                      </span>
                    </div>
                    <div 
                      v-if="leaderboardRanking.hiderRank !== null" 
                      class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-100 text-orange-800 rounded-lg text-xs font-semibold border border-orange-200"
                    >
                      <v-icon size="14" color="warning">mdi-eye-off</v-icon>
                      <span>Hider</span>
                      <span class="bg-orange-200 text-orange-900 px-1.5 py-0.5 rounded text-xs font-bold">
                        #{{ leaderboardRanking.hiderRank }}
                      </span>
                    </div>
                    <div 
                      v-if="leaderboardRanking.finderRank === null && leaderboardRanking.hiderRank === null" 
                      class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium border border-gray-200"
                    >
                      <v-icon size="14" color="grey">mdi-chart-line-variant</v-icon>
                      <span>Unranked</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <router-link
                  :to="{ path: '/', hash: '#leaderboard' }"
                  class="inline-flex items-center px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg text-xs font-medium border border-amber-200 shadow-sm"
                  title="View leaderboard"
                  @click.stop
                >
                  <v-icon size="16" class="mr-1" color="warning">mdi-trophy</v-icon>
                  Leaderboard
                </router-link>
                <v-icon 
                  :class="{ 'rotate-180': showDuckActivity }"
                  class="transition-transform duration-200"
                  color="primary"
                >
                  mdi-chevron-down
                </v-icon>
              </div>
            </div>
          </div>

          <!-- Collapsible Duck Activity Details -->
          <div v-if="showDuckActivity" class="mt-4 space-y-4">
            <!-- Found Ducks by Rarity -->
            <div v-if="ducksFoundByRarity" class="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-4 sm:p-6">
              <div class="flex items-center gap-3 mb-4">
                <div class="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <v-icon color="success" size="20">mdi-treasure-chest</v-icon>
                </div>
                <div>
                  <h4 class="text-lg font-semibold text-gray-900">Ducks Found</h4>
                  <p class="text-sm text-gray-600">
                    {{ getTotalFromRarity(ducksFoundByRarity) }} ducks discovered by rarity
                    <span class="ml-2 inline-flex items-center gap-1 text-amber-700">
                      <v-icon size="16" color="warning">mdi-trophy</v-icon>
                      <span class="font-medium">{{ getWeightedScoreFromRarity(ducksFoundByRarity) }}</span>
                    </span>
                  </p>
                </div>
              </div>
              
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div v-if="ducksFoundByRarity.COMMON > 0" class="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <img src="/duckRarity/COMMON.webp" alt="Common Duck" class="w-12 h-12 object-contain" />
                  <div>
                    <div class="flex items-center gap-2">
                      <span class="font-medium text-gray-900">Common</span>
                      <span class="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full font-medium">
                        {{ ducksFoundByRarity.COMMON }}
                      </span>
                    </div>
                    <p class="text-sm text-gray-600">Found</p>
                  </div>
                </div>
                
                <div v-if="ducksFoundByRarity.RARE > 0" class="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <img src="/duckRarity/RARE.webp" alt="Rare Duck" class="w-12 h-12 object-contain" />
                  <div>
                    <div class="flex items-center gap-2">
                      <span class="font-medium text-gray-900">Rare</span>
                      <span class="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
                        {{ ducksFoundByRarity.RARE }}
                      </span>
                    </div>
                    <p class="text-sm text-gray-600">Found</p>
                  </div>
                </div>
                
                <div v-if="ducksFoundByRarity.LEGENDARY > 0" class="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                  <img src="/duckRarity/LEGENDARY.webp" alt="Legendary Duck" class="w-12 h-12 object-contain" />
                  <div>
                    <div class="flex items-center gap-2">
                      <span class="font-medium text-gray-900">Legendary</span>
                      <span class="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full font-medium">
                        {{ ducksFoundByRarity.LEGENDARY }}
                      </span>
                    </div>
                    <p class="text-sm text-gray-600">Found</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Hidden Ducks by Rarity -->
            <div v-if="ducksHiddenByRarity" class="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-4 sm:p-6">
              <div class="flex items-center gap-3 mb-4">
                <div class="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <v-icon color="primary" size="20">mdi-map-marker-plus</v-icon>
                </div>
                <div>
                  <h4 class="text-lg font-semibold text-gray-900">Ducks Hidden</h4>
                  <p class="text-sm text-gray-600">
                    {{ getTotalFromRarity(ducksHiddenByRarity) }} ducks placed for others to find
                    <span class="ml-2 inline-flex items-center gap-1 text-amber-700">
                      <v-icon size="16" color="warning">mdi-trophy</v-icon>
                      <span class="font-medium">{{ getWeightedScoreFromRarity(ducksHiddenByRarity) }}</span>
                    </span>
                  </p>
                </div>
              </div>
              
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div v-if="ducksHiddenByRarity.COMMON > 0" class="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                  <img src="/duckRarity/COMMON.webp" alt="Common Duck" class="w-12 h-12 object-contain" />
                  <div>
                    <div class="flex items-center gap-2">
                      <span class="font-medium text-gray-900">Common</span>
                      <span class="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full font-medium">
                        {{ ducksHiddenByRarity.COMMON }}
                      </span>
                    </div>
                    <p class="text-sm text-gray-600">Hidden</p>
                  </div>
                </div>
                
                <div v-if="ducksHiddenByRarity.RARE > 0" class="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <img src="/duckRarity/RARE.webp" alt="Rare Duck" class="w-12 h-12 object-contain" />
                  <div>
                    <div class="flex items-center gap-2">
                      <span class="font-medium text-gray-900">Rare</span>
                      <span class="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
                        {{ ducksHiddenByRarity.RARE }}
                      </span>
                    </div>
                    <p class="text-sm text-gray-600">Hidden</p>
                  </div>
                </div>
                
                <div v-if="ducksHiddenByRarity.LEGENDARY > 0" class="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                  <img src="/duckRarity/LEGENDARY.webp" alt="Legendary Duck" class="w-12 h-12 object-contain" />
                  <div>
                    <div class="flex items-center gap-2">
                      <span class="font-medium text-gray-900">Legendary</span>
                      <span class="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full font-medium">
                        {{ ducksHiddenByRarity.LEGENDARY }}
                      </span>
                    </div>
                    <p class="text-sm text-gray-600">Hidden</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="!loading && cruises.length === 0" 
          class="text-center py-8 sm:py-12 bg-white/80 backdrop-blur-sm rounded-xl shadow-lg transition-colors"
        >
          <div class="relative w-20 sm:w-24 h-20 sm:h-24 mx-auto mb-4">
            <div class="absolute inset-0 bg-blue-100 rounded-full animate-pulse-slow"></div>
            <img src="/ducksImages/duckIcon.webp" class="w-20 sm:w-24 h-20 sm:h-24 relative z-10 drop-shadow-xl" />
          </div>
          <h3 class="text-lg sm:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-indigo-600 mb-2">No Cruises Yet!</h3>
          <p class="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base px-4">Time to launch your first duck expedition</p>
          <v-btn
            color="primary"
            size="large"
            prepend-icon="mdi-plus"
            @click="redirectToPricing"
            class="px-4 sm:px-6 py-2 sm:py-3"
          >
            <span class="hidden xs:inline">Create Your First Cruise</span>
            <span class="xs:hidden">Create Cruise</span>
          </v-btn>
        </div>

        <!-- Loading State -->
        <div v-else-if="loading" 
          class="flex flex-col items-center justify-center h-48 sm:h-64 bg-gradient-to-br from-white/90 via-white/85 to-white/80 backdrop-blur-sm rounded-xl transition-colors border border-gray-100"
        >
          <div class="relative">
            <img src="/ducksImages/duckLoader.webp" class="w-16 sm:w-20 h-16 sm:h-20 animate-bounce-slow" alt="Loading Duck" />
            <div class="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-8 sm:w-10 h-1 bg-blue-200 rounded-full blur-sm animate-pulse"></div>
          </div>
          <p class="text-gray-600 mt-3 sm:mt-4 animate-pulse text-sm sm:text-base">Loading your fleet...</p>
        </div>

        <!-- Cruise List -->
        <div v-else class="grid gap-4 sm:gap-6">
          <cruise-card
            v-for="cruise in cruises"
            :key="cruise.id"
            :cruise="cruise"
            :is-duck-list-visible="visibleDuckLists.has(cruise.id)"
            :is-resetting="resettingCruise === cruise.id"
            :is-downloading-q-r="downloadingQR === cruise.id"
            :has-new-messages="hasPulsatingIndicator(cruise.id)"
            :max-ducks-allowed="cruise.maxDucksAllowed || 0"
            :ducks-created="cruise.ducksCreated || 0"
            @preview="openPreviewDialog"
            @cruise-updated="handleCruiseUpdated"
            @reset="resetCruiseDucks"
            @download-qr="downloadQRCodes"
            @open-messages="openCruiseMessages"
            @toggle-ducks="toggleDuckList"
            @add-duck-with-warning="openAddDuckWithWarning"
            @duck-details="openDuckDetails"
            @duck-messages="openDuckMessages"
            @upgrade="openPackageDialog"
            @buy-more-ducks="openBuyMoreDucksDialog"
          />
        </div>

        <!-- Dialog Manager -->
        <dialog-manager
          :dialogs="dialogState"
          :loading-states="{ resetting: resettingCruise !== null }"
          :selected-items="{
            cruiseId: selectedCruiseId,
            cruiseForEdit: selectedCruiseForEdit,
            duck: selectedDuck,
            duckUuid: selectedDuckUuid,
            cruiseData: selectedCruiseForMoreDucks
          }"
          :cruise-name="getCruiseName(selectedCruiseId)"
          :duck-name="getDuckName(selectedDuckUuid)"
          :packages="packages"
          :loading-packages="loadingPackages"
          :checkout-processing="checkoutProcessing"
          :warning-message="warningForDuckForm"
          @update:dialogs="updateDialogState"
          @cruise-created="handleCruiseCreated"
          @cruise-updated="handleCruiseUpdated"
          @duck-created="handleDuckCreated"
          @duck-updated="handleDuckUpdated"
          @reset-confirmed="confirmResetDucks"
          @messages-updated="handleUpdate"
          @package-selected="handlePackageSelection"
          @buy-more-ducks="handleBuyMoreDucks"
          @refresh-cruises="refreshCruises"
        />

        <!-- DuckView Preview Dialog -->
        <duck-view-preview
          v-if="selectedCruiseForPreview"
          v-model="dialogState.preview"
          :cruise="selectedCruiseForPreview"
        />

        <!-- QR Template Selector Dialog -->
        <q-r-template-selector
          v-model="dialogState.qrTemplateSelector"
          :cruise-name="getCruiseName(selectedCruiseForQR)"
          :duck-count="getDuckCount(selectedCruiseForQR)"
          :loading="downloadingQR !== null"
          @template-selected="handleTemplateSelection"
        />

        <!-- Notifications -->
        <v-snackbar
          v-model="showSuccessNotification"
          color="success"
          :timeout="3000"
          location="top"
          rounded="pill"
          class="admin-notification"
        >
          <div class="flex items-center">
            <v-icon class="mr-2">mdi-check-circle</v-icon>
            {{ successMessage }}
          </div>
        </v-snackbar>

        <v-snackbar
          v-model="showErrorNotification"
          color="error"
          :timeout="3000"
          location="top"
          rounded="pill"
          class="admin-notification"
        >
          <div class="flex items-center">
            <v-icon class="mr-2">mdi-alert-circle</v-icon>
            {{ errorMessage }}
          </div>
        </v-snackbar>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import { onMounted, ref, onBeforeUnmount, shallowRef, reactive } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { Constant } from '@/config/constants';
import type { DetailedCruise, DetailedDuck, TrackingInformation } from '@/interfaces/cruise';
import { useAuthStore } from '@/stores/authStore';
import { UnderwaterBg } from '@/components/ui/underwaterBg';
import { usePackagesStore } from '@/stores/packagesStore';

// Import components
import AdminHeader from '@/components/admin/AdminHeader.vue';
import CruiseCard from '@/components/admin/CruiseCard.vue';
import DialogManager from '@/components/admin/DialogManager.vue';
import DuckViewPreview from '@/components/admin/DuckViewPreview.vue';
import QRTemplateSelector from '@/components/admin/QRTemplateSelector.vue';

// Package interface
interface Package {
  id: number;
  name: string;
  description: string;
  maxDucks: number;
  price: number;
  pricePerDuck?: number; // Add this as optional
  currency: string;
}

// Checkout session interface
interface CheckoutSessionResponse {
  sessionId: string;
  checkoutUrl: string;
}

// Duck count by rarity interface
interface DuckCountByRarity {
  COMMON: number;
  RARE: number;
  LEGENDARY: number;
}

// Leaderboard ranking interface
interface LeaderboardRanking {
  finderRank: number | null;
  hiderRank: number | null;
}

// My cruises response interface
interface MyCruisesResponse {
  cruises: DetailedCruise[];
  ducksFoundByRarity: DuckCountByRarity;
  ducksHiddenByRarity: DuckCountByRarity;
  username?: string;
  leaderboardRanking?: LeaderboardRanking;
}

const router = useRouter();
const authStore = useAuthStore();

// Dialog state management
const dialogState = reactive({
  editCruise: false,
  addDuck: false,
  duckDetails: false,
  resetConfirm: false,
  cruiseMessages: false,
  duckMessages: false,
  selectPackage: false,
  buyMoreDucks: false,
  createCruise: false,
  preview: false,
  qrTemplateSelector: false
});

// Selected items
const selectedCruiseId = ref<number | null>(null);
const selectedCruiseForEdit = ref<DetailedCruise | null>(null);
const selectedCruiseForPreview = ref<DetailedCruise | null>(null);
const selectedCruiseForQR = ref<number | null>(null);
const selectedDuck = ref<DetailedDuck | null>(null);
const selectedDuckUuid = ref<string | null>(null);
const pendingResetCruiseId = ref<number | null>(null);
const selectedCruiseForMoreDucks = ref<DetailedCruise | null>(null);

// Package and checkout related state
const packages = ref<Package[]>([]);
const loadingPackages = ref(false);
const checkoutProcessing = ref(false);

// Data and loading states
const cruises = shallowRef<DetailedCruise[]>([]);
const loading = ref(true);
const downloadingQR = ref<number | null>(null);
const resettingCruise = ref<number | null>(null);

// Notification states
const showSuccessNotification = ref(false);
const showErrorNotification = ref(false);
const successMessage = ref('');
const errorMessage = ref('');

// Message polling variables
const pollInterval = 15000; // 15 seconds interval for polling
let messagePollTimer: number;
const cruisesWithNewMessages = ref<Set<number>>(new Set());
const lastPollTime = ref<number>(Date.now());

// Reactive state to track visible duck lists
const visibleDuckLists = ref<Set<number>>(new Set());

// Buy more ducks related state
const buyingMoreDucks = ref(false);

// Duck activity state
const foundDucks = ref<any[]>([]);
const hiddenDucks = ref<any[]>([]);
const showDuckActivity = ref(false);
const ducksFoundByRarity = ref<DuckCountByRarity | null>(null);
const ducksHiddenByRarity = ref<DuckCountByRarity | null>(null);

// User profile state
const username = ref<string | null>(null);
const leaderboardRanking = ref<LeaderboardRanking | null>(null);

// Add packages store reference
const packagesStore = usePackagesStore();

// Add a new ref to store warning messages for the AddDuckForm
const warningForDuckForm = ref('');

// Lifecycle hooks
onMounted(async () => {
  try {
    loading.value = true;
    
    // Fetch cruises and packages in parallel for better performance
    const [cruisesResponse] = await Promise.all([
      axios.get(`${Constant.API_URL}/auth/my-cruises`),
      // Pre-fetch packages in background - this won't block the UI
      packagesStore.fetchPackages()
    ]);
    
    // Handle new MyCruisesResponse structure
    const myCruisesData: MyCruisesResponse | DetailedCruise[] = cruisesResponse.data;
    const isNewApiResponse = !Array.isArray(myCruisesData);
    
    cruises.value = sortCruises(isNewApiResponse ? (myCruisesData as MyCruisesResponse).cruises : myCruisesData as DetailedCruise[]);
    
    // Store duck statistics from the API response
    if (isNewApiResponse) {
      const response = myCruisesData as MyCruisesResponse;
      if (response.ducksFoundByRarity && response.ducksHiddenByRarity) {
        // Store the rarity statistics directly
        ducksFoundByRarity.value = response.ducksFoundByRarity;
        ducksHiddenByRarity.value = response.ducksHiddenByRarity;
        
        // Convert API statistics to our duck activity format for detailed view
        foundDucks.value = convertRarityStatsToList(response.ducksFoundByRarity, 'found');
        hiddenDucks.value = convertRarityStatsToList(response.ducksHiddenByRarity, 'hidden');
      }

      // Store user profile information
      if (response.username) {
        username.value = response.username;
      }
      if (response.leaderboardRanking) {
        leaderboardRanking.value = response.leaderboardRanking;
      }
    }
    
    // Initialize unread message counts and preload images
    preloadCruiseData();
    
    // Use packages from the store if already loaded
    if (packagesStore.packages.length > 0) {
      packages.value = packagesStore.packages.map((pkg) => ({
        ...pkg,
        // Ensure pricePerDuck is available
        pricePerDuck: pkg.pricePerDuck || pkg.price
      }));
    } else {
      // Fall back to direct API call if store fetch failed
      fetchPackages();
    }
  } catch (error) {
    console.error('Failed to fetch cruises:', error);
  } finally {
    loading.value = false;
  }

  // Set up message polling
  messagePollTimer = window.setInterval(pollForNewMessages, pollInterval);

  // Apply Firefox-specific optimizations
  applyBrowserOptimizations();
});

onBeforeUnmount(() => {
  if (messagePollTimer) {
    clearInterval(messagePollTimer);
  }
});

// Data processing functions
const preloadCruiseData = () => {
  if (!cruises.value) return;
  
  cruises.value.forEach(cruise => {
    // Preload duck images for better UX
    cruise.ducks.forEach(duck => {
      if (duck.image) {
        const img = new Image();
        img.src = getDuckImageUrl(cruise.id, duck.uuid);
      }
    });
    
    // Fetch tracking information for cruises with packages that don't allow additional ducks
    if (cruise.package && !cruise.package.allowsAdditionalDucks) {
      fetchTrackingInformation(cruise.id);
    }
  });
};

// Add this new function to fetch tracking information
const fetchTrackingInformation = async (cruiseId: number) => {
  try {
    const response = await axios.get<TrackingInformation>(
      `${Constant.API_URL}/auth/cruise/${cruiseId}/tracking`
    );
    
    if (response.data) {
      // Find the cruise and add tracking information
      const cruise = cruises.value.find(c => c.id === cruiseId);
      if (cruise) {
        cruise.tracking = response.data;
      }
    }
  } catch (error) {
    // Silently handle errors - tracking info might not be available
    console.log(`No tracking information available for cruise ${cruiseId}`);
  }
};

const sortCruises = (cruisesArray: DetailedCruise[]) => {
  const now = new Date();
  
  return [...cruisesArray].sort((a, b) => {
    const endDateA = new Date(a.endDate || a.startDate);
    const endDateB = new Date(b.endDate || b.startDate);
    
    // Check if cruises are active or expired
    const isExpiredA = endDateA < now;
    const isExpiredB = endDateB < now;
    
    // If one is expired and the other is not, prioritize active cruises
    if (isExpiredA && !isExpiredB) return 1;  // A is expired, move to end
    if (!isExpiredA && isExpiredB) return -1; // B is expired, move to end
    
    // If both are in the same category (active or expired), sort by date
    const startDateA = new Date(a.startDate).getTime();
    const startDateB = new Date(b.startDate).getTime();
    
    // If dates are the same, sort alphabetically by name
    return startDateA === startDateB ? 
      a.name.localeCompare(b.name) : 
      startDateA - startDateB;
  });
};

// Optimized image caching
const imageCache = new Map<string, string>();

const getDuckImageUrl = (cruiseId: number, duckUuid: string) => {
  const cacheKey = `${cruiseId}-${duckUuid}`;
  
  if (imageCache.has(cacheKey)) {
    return imageCache.get(cacheKey)!;
  }

  const duck = cruises.value
    .find(c => c.id === cruiseId)?.ducks
    .find(d => d.uuid === duckUuid);
  
  if (!duck?.image) {
    return '/ducksImages/duckIcon.webp';
  }
  
  const url = duck.image.startsWith('data:') 
    ? duck.image 
    : `data:image/webp;base64,${duck.image}`;

  imageCache.set(cacheKey, url);
  return url;
};

// Message polling system
const pollForNewMessages = async () => {
  if (cruises.value.length === 0) return;
  
  const now = Date.now();
  lastPollTime.value = now;
  
  try {
    // Poll cruises in parallel for better performance
    const pollPromises = cruises.value.map(cruise => pollCruiseMessage(cruise.id));
    const results = await Promise.allSettled(pollPromises);
    
    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        cruisesWithNewMessages.value.add(cruises.value[index].id);
      }
    });
  } catch (error) {
    console.error('Failed to poll for new messages:', error);
  }
};

const pollCruiseMessage = async (cruiseId: number): Promise<boolean> => {
  try {
    const response = await axios.get(
      `${Constant.API_URL}${Constant.POLL_CRUISE_MESSAGES}/${cruiseId}/poll-messages`
    );
    
    return response.status === 200;
  } catch (error) {
    return false;
  }
};

// Package and checkout methods
const fetchPackages = async () => {
  try {
    loadingPackages.value = true;
    
    // Check if packages are already in the store
    if (packagesStore.packages.length > 0) {
      packages.value = packagesStore.packages.map((pkg) => ({
        ...pkg,
        // Ensure pricePerDuck is available
        pricePerDuck: pkg.pricePerDuck || pkg.price
      }));
      return;
    }
    
    // Otherwise fetch directly
    const response = await axios.get(`${Constant.API_URL}${Constant.PACKAGES}`);
    
    // Map packages to ensure they have all required properties
    packages.value = response.data.map((pkg: any) => ({
      ...pkg,
      // If pricePerDuck doesn't exist, use price as fallback
      pricePerDuck: pkg.pricePerDuck || pkg.price
    }));
  } catch (error) {
    console.error('Failed to fetch packages:', error);
    errorMessage.value = 'Unable to load available packages';
    showErrorNotification.value = true;
  } finally {
    loadingPackages.value = false;
  }
};

const openPackageDialog = (cruiseId?: number) => {
  if (cruiseId) {
    selectedCruiseId.value = cruiseId;
  }
  dialogState.selectPackage = true;
};

const handlePackageSelection = async (packageData: { 
  packageId: number, 
  cruiseName: string, 
  quantity: number,
  startDate?: string,
  endDate?: string 
}) => {
  try {
    checkoutProcessing.value = true;
    
    const response = await axios.post<CheckoutSessionResponse>(
      `${Constant.API_URL}/auth/checkout`,
      packageData
    );
    
    if (response.data.checkoutUrl) {
      // Redirect to Stripe checkout
      window.location.href = response.data.checkoutUrl;
    } else {
      throw new Error('No checkout URL returned');
    }
  } catch (error) {
    console.error('Failed to create checkout session:', error);
    errorMessage.value = 'Unable to proceed to checkout';
    showErrorNotification.value = true;
  } finally {
    checkoutProcessing.value = false;
    dialogState.selectPackage = false;
  }
};

// Open the Buy More Ducks dialog
const openBuyMoreDucksDialog = (cruiseId: number) => {
  const cruise = cruises.value.find(c => c.id === cruiseId);
  if (cruise) {
    selectedCruiseId.value = cruiseId;
    selectedCruiseForMoreDucks.value = cruise;
    dialogState.buyMoreDucks = true;
  }
};

// Handle the Buy More Ducks form submission
const handleBuyMoreDucks = async (data: { cruiseId: number, quantity: number }) => {
  try {
    buyingMoreDucks.value = true;
    
    // Call the buy-more-ducks endpoint with the correct payload structure
    const response = await axios.post<{ checkoutUrl: string }>(
      `${Constant.API_URL}/auth/buy-more-ducks`,
      {
        cruiseId: data.cruiseId,
        quantity: data.quantity
      }
    );
    
    if (response.data.checkoutUrl) {
      // Redirect to Stripe checkout
      window.location.href = response.data.checkoutUrl;
    } else {
      throw new Error('No checkout URL returned');
    }
  } catch (error) {
    console.error('Failed to create checkout session for additional ducks:', error);
    errorMessage.value = 'Unable to proceed to checkout';
    showErrorNotification.value = true;
  } finally {
    buyingMoreDucks.value = false;
    dialogState.buyMoreDucks = false;
    selectedCruiseForMoreDucks.value = null;
  }
};

// Helper functions
const handleLogout = () => {
  authStore.logout();
  router.push('/');  
};

const redirectToPricing = () => {
  router.push('/#pricing');
};

const refreshCruises = async () => {
  try {
    const response = await axios.get(`${Constant.API_URL}/auth/my-cruises`);
    // Handle new MyCruisesResponse structure
    const myCruisesData: MyCruisesResponse | DetailedCruise[] = response.data;
    const isNewApiResponse = !Array.isArray(myCruisesData);
    
    cruises.value = sortCruises(isNewApiResponse ? (myCruisesData as MyCruisesResponse).cruises : myCruisesData as DetailedCruise[]);
  } catch (error) {
    console.error('Failed to refresh cruises:', error);
  }
};

// Message handling
const hasPulsatingIndicator = (cruiseId: number): boolean => {
  return cruisesWithNewMessages.value.has(cruiseId);
};

const getCruiseName = (cruiseId: number | null): string => {
  if (!cruiseId) return '';
  return cruises.value.find(c => c.id === cruiseId)?.name || 'Cruise';
};

const getDuckCount = (cruiseId: number | null): number => {
  if (!cruiseId) return 0;
  return cruises.value.find(c => c.id === cruiseId)?.ducks.length || 0;
};

const getDuckName = (duckUuid: string | null): string => {
  if (!duckUuid) return '';
  for (const cruise of cruises.value) {
    const duck = cruise.ducks.find(d => d.uuid === duckUuid);
    if (duck) return `Duck ${duck.id}`;
  }
  return 'Duck';
};

// Toggle duck activity section
const toggleDuckActivity = () => {
  showDuckActivity.value = !showDuckActivity.value;
};

// Get total count from rarity object
const getTotalFromRarity = (rarityObj: DuckCountByRarity | null) => {
  if (!rarityObj) return 0;
  return (rarityObj.COMMON || 0) + (rarityObj.RARE || 0) + (rarityObj.LEGENDARY || 0);
};

// Get weighted score (Legendary=3, Rare=2, Common=1) to match leaderboard
const getWeightedScoreFromRarity = (rarityObj: DuckCountByRarity | null) => {
  if (!rarityObj) return 0;
  const common = rarityObj.COMMON || 0;
  const rare = rarityObj.RARE || 0;
  const legendary = rarityObj.LEGENDARY || 0;
  return (legendary * 3) + (rare * 2) + (common * 1);
};

// Convert rarity statistics to duck list format for display
const convertRarityStatsToList = (rarityStats: DuckCountByRarity, type: 'found' | 'hidden') => {
  const ducks: any[] = [];
  let duckId = 1;
  
  if (rarityStats.COMMON > 0) {
    for (let i = 0; i < rarityStats.COMMON; i++) {
      ducks.push({
        id: duckId++,
        uuid: `${type}-common-${i}`,
        rarity: 'COMMON',
        foundDate: type === 'found' ? new Date().toISOString() : null,
        cruiseStartDate: null
      });
    }
  }
  
  if (rarityStats.RARE > 0) {
    for (let i = 0; i < rarityStats.RARE; i++) {
      ducks.push({
        id: duckId++,
        uuid: `${type}-rare-${i}`,
        rarity: 'RARE',
        foundDate: type === 'found' ? new Date().toISOString() : null,
        cruiseStartDate: null
      });
    }
  }
  
  if (rarityStats.LEGENDARY > 0) {
    for (let i = 0; i < rarityStats.LEGENDARY; i++) {
      ducks.push({
        id: duckId++,
        uuid: `${type}-legendary-${i}`,
        rarity: 'LEGENDARY',
        foundDate: type === 'found' ? new Date().toISOString() : null,
        cruiseStartDate: null
      });
    }
  }
  
  return ducks;
};

// Dialog state management
const updateDialogState = (newDialogState: typeof dialogState) => {
  // If addDuck dialog is being closed, reset the warning message
  if (dialogState.addDuck && !newDialogState.addDuck) {
    warningForDuckForm.value = '';
  }

  Object.assign(dialogState, newDialogState);
};

// Action handlers
const openAddDuckDialog = async (cruiseId: number) => {
  try {
    // First refresh cruises to get the most up-to-date count
    await refreshCruises();
    
    // Then set up the dialog
    selectedCruiseId.value = cruiseId;
    
    // Find the cruise with fresh data
    const cruise = cruises.value.find(c => c.id === cruiseId);
    if (cruise) {
      // Store the updated cruise data for AddDuckForm to access duck limits
      selectedCruiseForEdit.value = cruise;
    }
    
    // Open the dialog after refreshing data
    dialogState.addDuck = true;
  } catch (error) {
    console.error('Failed to refresh cruise data:', error);
    errorMessage.value = 'Failed to load updated duck information';
    showErrorNotification.value = true;
    
    // Still try to open the dialog with existing data as fallback
    selectedCruiseId.value = cruiseId;
    const cruise = cruises.value.find(c => c.id === cruiseId);
    if (cruise) {
      selectedCruiseForEdit.value = cruise;
    }
    dialogState.addDuck = true;
  }
};

// Add a new method to handle the warning message case
const openAddDuckWithWarning = async (cruiseId: number, warningMessage: string) => {
  // Store the warning message
  warningForDuckForm.value = warningMessage;
  
  // Then call the normal addDuck method
  await openAddDuckDialog(cruiseId);
};

const openPreviewDialog = (cruise: DetailedCruise) => {
  selectedCruiseForPreview.value = { ...cruise }; // Clone to avoid direct mutation
  dialogState.preview = true;
};

const openDuckDetails = (duck: DetailedDuck) => {
  selectedDuck.value = duck;
  dialogState.duckDetails = true;
};

const openCruiseMessages = (cruiseId: number) => {
  selectedCruiseId.value = cruiseId;
  dialogState.cruiseMessages = true;

  // Remove from the set of cruises with new messages
  cruisesWithNewMessages.value.delete(cruiseId);

  // Reset unread message count for the cruise
  const cruise = cruises.value.find(c => c.id === cruiseId);
  if (cruise) {
    cruise.unreadMessageCount = 0;
  }
};

const openDuckMessages = (duckUuid: string) => {
  selectedDuckUuid.value = duckUuid;
  dialogState.duckMessages = true;
};

// Toggle visibility of the duck list for a specific cruise
const toggleDuckList = (cruiseId: number) => {
  if (visibleDuckLists.value.has(cruiseId)) {
    visibleDuckLists.value.delete(cruiseId);
  } else {
    visibleDuckLists.value.add(cruiseId);
  }
};

const downloadQRCodes = async (cruiseId: number) => {
  // Open template selector instead of directly downloading
  selectedCruiseForQR.value = cruiseId;
  dialogState.qrTemplateSelector = true;
};

const handleTemplateSelection = async (template: string) => {
  if (!selectedCruiseForQR.value) return;
  
  try {
    downloadingQR.value = selectedCruiseForQR.value;
    const response = await axios.get(
      `${Constant.API_URL}${Constant.GENERATE_QR_CODES}/${selectedCruiseForQR.value}`,
      { 
        responseType: 'blob',
        params: { template } // Pass the selected template to the backend
      }
    );
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `qrcodes-cruise-${selectedCruiseForQR.value}-${template}.zip`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    // Show success message
    successMessage.value = 'QR codes generated successfully!';
    showSuccessNotification.value = true;
    
    // Close the template selector
    dialogState.qrTemplateSelector = false;
    selectedCruiseForQR.value = null;
  } catch (error) {
    console.error('Failed to download QR codes:', error);
    errorMessage.value = 'Failed to generate QR codes';
    showErrorNotification.value = true;
  } finally {
    downloadingQR.value = null;
  }
};

const resetCruiseDucks = (cruiseId: number) => {
  pendingResetCruiseId.value = cruiseId;
  dialogState.resetConfirm = true;
};

const confirmResetDucks = async () => {
  if (!pendingResetCruiseId.value) return;

  try {
    resettingCruise.value = pendingResetCruiseId.value;
    await axios.post(`${Constant.API_URL}${Constant.RESET_CRUISE_DUCKS}`, {
      cruiseID: pendingResetCruiseId.value
    });
    
    // Refresh cruise list to show updated duck statuses
    await refreshCruises();
    dialogState.resetConfirm = false;
    
    // Show success notification
    successMessage.value = 'All ducks have been reset';
    showSuccessNotification.value = true;
  } catch (error) {
    console.error('Failed to reset cruise ducks:', error);
    errorMessage.value = 'Failed to reset ducks';
    showErrorNotification.value = true;
  } finally {
    resettingCruise.value = null;
    pendingResetCruiseId.value = null;
  }
};

// Event handlers
const handleCruiseCreated = async (newCruise: DetailedCruise) => {
  try {
    await refreshCruises();
    dialogState.createCruise = false;
    successMessage.value = 'Cruise created successfully!';
    showSuccessNotification.value = true;
  } catch (error) {
    // Fallback: add the cruise we have even if incomplete
    cruises.value = sortCruises([...cruises.value, newCruise]);
    dialogState.createCruise = false;
  }
};

const handleCruiseUpdated = async () => {
  try {
    loading.value = true;
    await refreshCruises();
    dialogState.editCruise = false;
    successMessage.value = 'Cruise updated successfully!';
    showSuccessNotification.value = true;
  } catch (error) {
    errorMessage.value = 'Failed to update cruise data';
    showErrorNotification.value = true;
  } finally {
    selectedCruiseForEdit.value = null;
    loading.value = false;
  }
};

const handleDuckCreated = async () => {
  try {
    await refreshCruises();
    dialogState.addDuck = false;
  } catch (error) {
    console.error('Failed to refresh cruises after duck creation:', error);
  }
};

const handleDuckUpdated = async () => {
  try {
    // Refresh cruises to get updated duck status
    await refreshCruises();
    successMessage.value = 'Duck updated successfully!';
    showSuccessNotification.value = true;
  } catch (error) {
    console.error('Failed to refresh cruises after duck update:', error);
  }
};

const handleUpdate = async (update: { 
  cruiseId?: number; 
  duckUuid?: string; 
  clearAll?: boolean; 
  refreshCruise?: boolean;
  openDuckMessages?: boolean;
  messageId?: number;
}) => {
  if (update.clearAll && update.cruiseId) {
    // Clear notifications for the cruise and all its ducks
    const cruise = cruises.value.find(c => c.id === update.cruiseId);
    if (cruise) {
      cruise.unreadMessageCount = 0;
      cruise.ducks.forEach(duck => (duck.unreadMessageCount = 0));
    }
  } else if (update.refreshCruise) {
    // Refresh cruise notifications from the server
    await refreshCruises();
  }
  
  // Handle opening duck messages when a user clicks on a message from a duck
  if (update.openDuckMessages && update.duckUuid) {
    // Close the cruise messages dialog
    dialogState.cruiseMessages = false;
    
    // Set the selected duck UUID and open the duck messages dialog
    selectedDuckUuid.value = update.duckUuid;
    
    // Use setTimeout to ensure the first dialog is fully closed before opening the second
    setTimeout(() => {
      dialogState.duckMessages = true;
    }, 150);
  }
};

// Browser optimizations
const applyBrowserOptimizations = () => {
  const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
  
  if (isFirefox) {
    document.documentElement.style.setProperty('--animation-duration', '0.2s');
    document.documentElement.style.scrollBehavior = 'smooth';
  }
};
</script>

<style scoped>
/* Animations */
@keyframes bounce-slow {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.animate-bounce-slow {
  animation: bounce-slow 2s infinite ease-in-out;
}

@keyframes pulse-slow {
  0%, 100% { opacity: 0.6; transform: scale(0.98); }
  50% { opacity: 0.8; transform: scale(1); }
}

.animate-pulse-slow {
  animation: pulse-slow 3s infinite alternate;
}

/* Firefox-specific optimizations */
@-moz-document url-prefix() {
  .firefox-optimize {
    transform: translate3d(0, 0, 0);
    backface-visibility: hidden;
    perspective: 1000px;
    will-change: transform;
  }

  /* Optimize scrolling performance */
  * {
    scrollbar-width: thin;
    scrollbar-color: rgba(155, 155, 155, 0.5) transparent;
  }

  /* Optimize animations */
  .animate-fade-in {
    animation: none;
    opacity: 1;
  }

  /* Improve rendering of backdrop blur */
  .backdrop-blur-sm {
    backdrop-filter: blur(8px);
    -moz-backdrop-filter: blur(8px);
  }

  /* Optimize fixed positioning */
  .fixed {
    transform: translateZ(0);
    will-change: transform;
  }
}

/* Enhanced notification styles */
:deep(.admin-notification) {
  border-radius: 40px !important;
  font-weight: 500 !important;
}

:deep(.v-snackbar__content) {
  padding: 12px 24px;
}


/* Ensure proper spacing and alignment for mobile */
@media (max-width: 640px) {
  .text-justify {
    text-align: justify;
  }

  .break-all {
    word-break: break-word;
  }
}

/* Safe area insets for modern devices */
@supports (padding: max(0px)) {
  main {
    padding-left: env(safe-area-inset-left, 0);
    padding-right: env(safe-area-inset-right, 0);
    padding-bottom: env(safe-area-inset-bottom, 0);
  }
}

/* Improve touch targets for all interactive elements */
:deep(button),
:deep(a),
:deep(.v-btn) {
  min-height: 44px;
  min-width: 44px;
  touch-action: manipulation;
}

/* Reduce animation for users who prefer reduced motion */
@media (prefers-reduced-motion: reduce) {
  .animate-bounce-slow,
  .animate-pulse-slow {
    animation: none !important;
  }
}
</style>
